import { useEffect, useRef } from "react";

type UseInfiniteScrollProps = {
  hasMore: boolean;
  onLoadMore: () => void;
  itemCount: number;
  scrollableTargetId?: string;
};

/**
 * Observes a sentinel element and triggers `onLoadMore` when it becomes
 * visible. Complements `react-infinite-scroll-component` by also fetching the
 * next page when there is no scroll yet (small viewport / few items).
 */
export const useInfiniteScroll = ({
  hasMore,
  onLoadMore,
  itemCount,
  scrollableTargetId = "page-content",
}: UseInfiniteScrollProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore || !sentinelRef.current) {
      return;
    }
    const root = document.getElementById(scrollableTargetId);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMore();
        }
      },
      { root: root ?? null }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [itemCount, hasMore, onLoadMore, scrollableTargetId]);

  return { sentinelRef };
};
